import { createPortal } from "react-dom";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { Form } from "../../../../../components/ui/common/form";
import ExternalTransportIdentificationSection from "./components/ExternalTransportIdentificationSection";
import ExternalTransportNotesSection from "./components/ExternalTransportNotesSection";
import ExternalTransportRouteSection from "./components/ExternalTransportRouteSection";
import TransportDrawerLayout, { TransportDrawerLoading } from "./components/TransportDrawerLayout";
import {
  ExternalTransportModalData,
  ExternalTransportToolVariant
} from "./externalTransport.types";
import { useExternalTransportBaseScreen } from "./hooks/useExternalTransportBaseScreen";

interface ExternalTransportBaseScreenProps {
  modalData: ExternalTransportModalData;
  variant?: ExternalTransportToolVariant;
}

const ExternalTransportBaseScreen = ({
  modalData,
  variant = "mapping"
}: ExternalTransportBaseScreenProps) => {
  const screen = useExternalTransportBaseScreen({ modalData, variant });

  if (screen.isPageLoading) {
    return <TransportDrawerLoading />;
  }

  return (
    <>
      <Form {...screen.form}>
        <TransportDrawerLayout
          formId="external-transport-form"
          onSubmit={screen.handleSubmit(screen.onSubmit)}
          saveDisabled={screen.saveDisabled}
          saveTitle={screen.saveTitle}
          deleteDisabled={screen.deleteDisabled}
          onDeleteClick={() => screen.setVisibleDeleteModal(true)}
        >
          <div className="tw-space-y-6">
            <section className="tw-space-y-3">
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Identification
              </p>
              <ExternalTransportIdentificationSection
                control={screen.control}
                disabled={screen.isFormDisabled}
                transportationTypes={screen.transportationTypes}
                typesLoading={screen.transportationTypesFetching}
                typesError={screen.transportationTypesError}
                onRetryTypes={() => {
                  void screen.refetchTransportationTypes();
                }}
              />
            </section>

            <section className="tw-space-y-3">
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Route
              </p>
              <ExternalTransportRouteSection
                control={screen.control}
                disabled={screen.isFormDisabled}
                weightDisabled={screen.weightDisabled}
                distanceDisabled={screen.distanceDisabled}
              />
            </section>

            {screen.supportsNotes && (
              <ExternalTransportNotesSection
                notesRef={screen.notesRef}
                parentId={screen.externalTransportId}
                disabled={screen.isFormDisabled}
              />
            )}
          </div>
        </TransportDrawerLayout>
      </Form>

      {screen.visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={screen.visibleDeleteModal}
            closeModal={() => screen.setVisibleDeleteModal(false)}
            cancel={() => screen.setVisibleDeleteModal(false)}
            approve={screen.handleDelete}
            title="Delete External Transport"
            subTitle="Are you sure you want to remove the external transport? Please consider you cannot recover these data after remove"
          />,
          document.body
        )}
    </>
  );
};

export default ExternalTransportBaseScreen;
